import type { ActivityEvent } from "@/features/stationAdmin/api/stationActivityApi";

/**
 * Presentation helpers for the Recent Activity feed: time-bucket grouping of
 * an already reverse-chronological event list. Pure functions (injectable
 * `now`) so bucketing and labels are unit-testable without fake timers.
 */

export type ActivityGroupBy = "none" | "day" | "week" | "month";

export const ACTIVITY_GROUP_OPTIONS: Array<{
  value: ActivityGroupBy;
  label: string;
}> = [
  { value: "none", label: "None" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

export interface ActivityGroup {
  /** Stable bucket key (React key). */
  key: string;
  /** Human header ("Today", "Week of Jul 6", "July 2026"); "" in the flat view. */
  label: string;
  events: ActivityEvent[];
}

/** Local midnight of the given date. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Local midnight of the week's Sunday (US convention, matching the app's locale defaults). */
function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  day.setDate(day.getDate() - day.getDay());
  return day;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(d: Date, now: Date): string {
  const target = startOfDay(d).getTime();
  const today = startOfDay(now);
  if (target === today.getTime()) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (target === yesterday.getTime()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

function weekLabel(d: Date, now: Date): string {
  const target = startOfWeek(d).getTime();
  const thisWeek = startOfWeek(now);
  if (target === thisWeek.getTime()) return "This week";
  const lastWeek = new Date(thisWeek);
  lastWeek.setDate(lastWeek.getDate() - 7);
  if (target === lastWeek.getTime()) return "Last week";
  const start = new Date(target);
  return `Week of ${start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(start.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  })}`;
}

/**
 * Public label for a local day ("Today", "Yesterday", "Tue, Jul 7", with the
 * year appended for other years). Shared by the feed's day groups and the
 * dashboard's day chips so both surfaces name days identically.
 */
export function formatActivityDay(d: Date, now: Date = new Date()): string {
  return dayLabel(d, now);
}

function monthLabel(d: Date, now: Date): string {
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) {
    return "This month";
  }
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

/**
 * Buckets events into labeled time groups, preserving the incoming
 * (reverse-chronological) order both across and within groups — so group
 * headers appear newest-first. Events with unparseable timestamps (upstream
 * only filters empty strings) collect into a trailing "Undated" bucket rather
 * than being dropped. `groupBy: "none"` returns a single unlabeled group so
 * callers can render flat and grouped views through one code path.
 */
export function groupActivityEvents(
  events: ActivityEvent[],
  groupBy: ActivityGroupBy,
  now: Date = new Date(),
): ActivityGroup[] {
  if (events.length === 0) return [];
  if (groupBy === "none") return [{ key: "all", label: "", events }];

  const groups = new Map<string, ActivityGroup>();
  const undated: ActivityEvent[] = [];

  for (const event of events) {
    const when = new Date(event.timestamp);
    if (Number.isNaN(when.getTime())) {
      undated.push(event);
      continue;
    }
    let key: string;
    let label: string;
    if (groupBy === "day") {
      key = dayKey(startOfDay(when));
      label = dayLabel(when, now);
    } else if (groupBy === "week") {
      key = dayKey(startOfWeek(when));
      label = weekLabel(when, now);
    } else {
      key = `${when.getFullYear()}-${when.getMonth()}`;
      label = monthLabel(when, now);
    }
    const existing = groups.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      groups.set(key, { key, label, events: [event] });
    }
  }

  const result = [...groups.values()];
  if (undated.length > 0) {
    result.push({ key: "undated", label: "Undated", events: undated });
  }
  return result;
}
